import React, { Component } from "react";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import Loading from "../../../common/components/loading/loading";
import DualListBox from "../../../common/components/form/dualListBox";
import { withTranslation } from "react-i18next";
import { getUserAccountsByOrganization } from "../../../common/services/auth/userAccountOrganizationService";
import {
  getUserAccountSubscriptionsByOrganizationSubscription,
  getUserAccountSubscription,
  addUserAccountSubscription,
  updateUserAccountSubscription,
} from "../../../common/services/marketdata/userAccountSubscriptionService";

class OrganizationSubscriptionUserAccounts extends Component {
  state = {
    options: [],
    selected: [],
    userAccountSubscriptionMap: {}, // Maps userAccountId to full subscription object
    isLoading: true,
  };

  async componentDidMount() {
    const { organizationSubscriptionId, organizationId } = this.props;

    try {
      // Get all users in the organization
      const allUsersInOrg = await getUserAccountsByOrganization(organizationId);

      // Get user accounts already assigned to this organization subscription
      const userAccountSubscriptions =
        (await getUserAccountSubscriptionsByOrganizationSubscription(
          organizationSubscriptionId
        )) || [];

      // Create a map of userAccountId to full subscription object for efficient updates
      // This includes both active and inactive subscriptions
      const userAccountSubscriptionMap = {};
      (userAccountSubscriptions || []).forEach((sub) => {
        if (sub && sub.userAccountId) {
          userAccountSubscriptionMap[sub.userAccountId] = sub;
        }
      });

      // Filter to only active subscriptions for the selected list
      const activeSubscriptions = (userAccountSubscriptions || []).filter(
        (sub) =>
          sub &&
          sub.userAccountId &&
          sub.active !== false &&
          sub.active !== "false" &&
          sub.active !== 0
      );

      // Use ALL subscriptions for selected list - API likely only returns active ones
      // If API returns both, we filter above, but as fallback show all
      const selectedUserIds = (activeSubscriptions || [])
        .map((sub) => sub && (sub.userAccountId ? sub.userAccountId : null))
        .filter(Boolean);

      this.setState({
        options: this.mapToOptionsViewModel(allUsersInOrg),
        selected: selectedUserIds,
        userAccountSubscriptionMap,
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      toast.error(this.props.t("LoadFailed"));
      this.setState({ isLoading: false });
    }
  }

  mapToOptionsViewModel(users) {
    return users.map((user) => {
      const fullName =
        user.firstName || user.lastName
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
          : "";

      return {
        value: user.userAccountId,
        label: fullName ? `${user.login} (${fullName})` : user.login,
      };
    });
  }

  mapToSelectedViewModel(userAccountSubscriptions) {
    return userAccountSubscriptions.map(
      (subscription) => subscription.userAccountId
    );
  }

  async handleChange(selected, selection, controlKey) {
    const { organizationSubscriptionId } = this.props;
    const eventId = uuid();

    if (controlKey !== "available" && controlKey !== "selected") {
      toast.warning(this.props.t("UnexpectedError"));
      return null;
    }

    this.setState({ selected });

    for (const userAccountId of selection) {
      if (controlKey === "available") {
        try {
          let response;
          const existingSubscription =
            this.state.userAccountSubscriptionMap[userAccountId];

          if (existingSubscription && !existingSubscription.active) {
            // Reactivate existing inactive subscription
            const fullSubscription = await getUserAccountSubscription(
              existingSubscription.userAccountSubscriptionId
            );
            response = await updateUserAccountSubscription({
              ...fullSubscription,
              active: true,
              eventId,
            });
          } else {
            // Create new subscription
            response = await addUserAccountSubscription({
              userAccountSubscriptionId: uuid(), // Generate UUID for new records
              userAccountId,
              organizationSubscriptionId,
              contributor: false,
              active: true,
              eventId,
            });
          }
          // Add/update map after successful addition/reactivation
          const updatedMap = { ...this.state.userAccountSubscriptionMap };
          updatedMap[userAccountId] = response;
          this.setState({ userAccountSubscriptionMap: updatedMap });
          toast.success(this.props.t("UserAccountSubscriptionAdded"));
        } catch (ex) {
          console.log(ex);
          const index = selected.indexOf(userAccountId);
          if (index > -1) selected.splice(index, 1);
          toast.warning(this.props.t("UserAccountSubscriptionAdditionFailed"));
        }
      } else if (controlKey === "selected") {
        try {
          const subscription =
            this.state.userAccountSubscriptionMap[userAccountId];

          if (subscription) {
            // Get the full subscription object to update it
            const fullSubscription = await getUserAccountSubscription(
              subscription.userAccountSubscriptionId
            );

            // Update the subscription to set active to false
            const updatedSubscription = await updateUserAccountSubscription({
              ...fullSubscription,
              active: false,
              eventId,
            });

            // Update the map with the deactivated subscription
            const updatedMap = { ...this.state.userAccountSubscriptionMap };
            updatedMap[userAccountId] = updatedSubscription;
            this.setState({ userAccountSubscriptionMap: updatedMap });
            toast.success(this.props.t("UserAccountSubscriptionRemoved"));
          }
        } catch (ex) {
          console.log(ex);
          selected.push(userAccountId);
          toast.warning(this.props.t("UserAccountSubscriptionRemovalFailed"));
        }
      }
    }

    this.setState({ selected });
  }

  render() {
    const { options, selected, isLoading } = this.state;
    const { allowEdit } = this.props;

    if (isLoading) return <Loading />;

    return (
      <div>
        <DualListBox
          canFilter
          className="rdl-hide-move-all"
          disabled={!allowEdit}
          options={options}
          selected={selected}
          onChange={(selected, selection, controlKey) =>
            this.handleChange(selected, selection, controlKey)
          }
        />
      </div>
    );
  }
}

export default withTranslation(["auth"])(OrganizationSubscriptionUserAccounts);
